import { useGetDriverFaceClusters } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { endOfDay, startOfDay } from '@cobliteam/utils/date';
import { useMemo } from 'react';
import type { Photo, PhotoCluster, PhotoClustersArgs, PhotoClustersResult } from '../types';

const MAX_SAMPLES_PER_CLUSTER = 3;

/**
 * Fetches the pool of unidentified (not-yet-nominated) driver face clusters — the same query
 * `NotIdentifiedDriversTable` uses. These are the candidates the user can link to the current
 * driver. Pagination and the vehicle filter are handled server-side.
 */
export const useDriverPhotoClusters = (args: PhotoClustersArgs): PhotoClustersResult => {
  const vehicleIds = args.filters.vehicleId ? [args.filters.vehicleId] : undefined;
  const { from, to } = args.filters.dateRange;
  const fromTimestamp = from ? startOfDay(from).toISOString() : undefined;
  const toTimestamp = to ? endOfDay(to).toISOString() : undefined;

  const { data, isLoading, isFetching, isError, refetch } = useGetDriverFaceClusters(
    undefined,
    { page: Math.max(0, args.page - 1), size: args.pageSize },
    false,
    vehicleIds,
    fromTimestamp,
    toTimestamp,
    MAX_SAMPLES_PER_CLUSTER
  );

  const clusters = useMemo<PhotoCluster[]>(
    () =>
      (data?.content ?? []).flatMap((cluster) => {
        if (!cluster) return [];
        const photos: Photo[] = (cluster.sampleImages ?? []).flatMap((img, idx) => {
          if (!img) return [];
          return [
            {
              id: `${cluster.id}-${idx}`,
              imageUrl: img.imageUrl,
              capturedAt: img.capturedAt,
              latitude: img.latitude ?? undefined,
              longitude: img.longitude ?? undefined,
              vehicle: img.vehicle
                ? {
                    id: img.vehicle.id,
                    licensePlate: img.vehicle.licensePlate ?? undefined,
                    alias: img.vehicle.alias ?? undefined,
                  }
                : undefined,
            },
          ];
        });
        return photos.length > 0 ? [{ id: cluster.id, photos }] : [];
      }),
    [data]
  );

  return {
    clusters,
    totalGroups: data?.totalElements ?? 0,
    shownGroups: clusters.length,
    totalPages: data?.totalPages ?? 0,
    isLoading: isLoading || isFetching,
    isError,
    refetch,
  };
};
